import { ConvertPrimitiveToVectorNetworkCommand } from "@/commands/vectorCommands";
import { UpdateNodeTransformCommand } from "@/commands/nodeCommands";
import { findSceneNode, getAllSceneNodes } from "@/document/model/document";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";
import { enterTextMode } from "@/editor/shell/TextEditSession";
import type { TextEditSessionRef } from "@/editor/shell/TextEditSession";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { rectCenter } from "@/lib/math/rotation";

export function enterVectorMode(host: EditorCommandHost, nodeId: string | undefined): void {
  selectionStore.setFreshCutVertexIds([]);
  if (!nodeId) {
    uiStore.setMessage("Select an unlocked shape or vector network to edit");
    return;
  }

  const primaryNode = getAllSceneNodes(documentStore.getDocument()).find(node => node.id === nodeId);
  if (!primaryNode || isNodeLockedForEditing(documentStore.getDocument(), primaryNode.id)) {
    uiStore.setMessage("Select an unlocked shape or vector network to edit");
    return;
  }

  if (primaryNode.type === "vectorNetwork") {
    const nextSelection = host.selectionSnapshot({
      selectedNodeIds: [primaryNode.id],
      primarySelectedNodeId: primaryNode.id,
      mode: "vectorEdit",
      selectedVertexId: undefined,
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    if (primaryNode.transform.rotation !== 0 && primaryNode.transform.x === 0 && primaryNode.transform.y === 0) {
      const context = host.commandContext();
      host.history.execute(
        new UpdateNodeTransformCommand(primaryNode.id, primaryNode.transform, { ...primaryNode.transform, ...rectCenter(primaryNode.bounds) }),
        context,
        { afterSelection: nextSelection },
      );
      documentStore.setDocument(context.document);
      host.applySelectionSnapshot(nextSelection);
    } else {
      host.updateSelection(nextSelection, true);
    }
    uiStore.setMessage(`Editing ${primaryNode.name}`);
    return;
  }

  if (primaryNode.type === "primitive") {
    const nextSelection = host.selectionSnapshot({
      selectedNodeIds: [primaryNode.id],
      primarySelectedNodeId: primaryNode.id,
      mode: "vectorEdit",
      selectedVertexId: undefined,
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    const context = host.commandContext();
    host.history.execute(new ConvertPrimitiveToVectorNetworkCommand(primaryNode.id), context, { afterSelection: nextSelection });
    documentStore.setDocument(context.document);
    host.applySelectionSnapshot(nextSelection);
    uiStore.setMessage(`Editing ${primaryNode.name}`);
    return;
  }

  uiStore.setMessage("Select a shape or vector network to edit");
}

export function exitVectorMode(host: EditorCommandHost): void {
  selectionStore.setFreshCutVertexIds([]);
  host.updateSelection(
    host.selectionSnapshot({ mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined }),
    true,
  );
  uiStore.setMessage("Exited vector editing");
}

export function cancelPathDrawing(host: EditorCommandHost): boolean {
  const selection = selectionStore.getSelection();
  if (selection.mode !== "vectorEdit") {
    return false;
  }

  const hasVectorSubselection = Boolean(
    selection.selectedVertexId ||
    (selection.selectedVertexIds?.length ?? 0) > 0 ||
    (selection.selectedEdgeIds?.length ?? 0) > 0 ||
    selection.selectedHandle,
  );
  if (!hasVectorSubselection) {
    return false;
  }

  host.updateSelection(
    host.selectionSnapshot({ selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined }),
    true,
  );
  const activeTool = selectionStore.tool.getValue().activeTool;
  uiStore.setMessage(activeTool === "path" && selection.selectedVertexId ? "Path drawing canceled" : "Vector selection cleared");
  return true;
}

export function enterEditMode(
  host: EditorCommandHost,
  textSessionRef: TextEditSessionRef,
  primarySelectedNodeId: string | undefined,
): void {
  const animationPanelOpen = uiStore.animation.getValue().panelOpen;
  if (animationPanelOpen) {
    enterVectorTargeting(host, primarySelectedNodeId);
    return;
  }

  const primaryNode = primarySelectedNodeId
    ? getAllSceneNodes(documentStore.getDocument()).find(node => node.id === primarySelectedNodeId)
    : undefined;
  if (primaryNode?.type === "text") {
    enterTextMode(host, textSessionRef, primaryNode.id);
    return;
  }

  enterVectorMode(host, primarySelectedNodeId);
}

export function enterVectorTargeting(host: EditorCommandHost, nodeId: string | undefined): void {
  if (!nodeId) {
    uiStore.setMessage("Select a shape or vector network to target");
    return;
  }

  const doc = documentStore.getDocument();
  const primaryNode = getAllSceneNodes(doc).find(node => node.id === nodeId);
  if (!primaryNode || isNodeLockedForEditing(doc, primaryNode.id)) {
    uiStore.setMessage("Select an unlocked shape or vector network to target");
    return;
  }

  if (primaryNode.type === "primitive") {
    const nextSelection = host.selectionSnapshot({
      selectedNodeIds: [primaryNode.id],
      primarySelectedNodeId: primaryNode.id,
      mode: "select",
      selectedVertexId: undefined,
      selectedVertexIds: [],
      selectedEdgeIds: [],
      selectedHandle: undefined,
    });
    const context = host.commandContext();
    host.history.execute(new ConvertPrimitiveToVectorNetworkCommand(primaryNode.id), context, { afterSelection: nextSelection });
    documentStore.setDocument(context.document);
    host.applySelectionSnapshot(nextSelection);
    const converted = getAllSceneNodes(documentStore.getDocument()).find(n => n.id === primaryNode.id);
    uiStore.patchAnimation({ vectorTargetNodeId: converted?.id ?? primaryNode.id });
    uiStore.setMessage(`Targeting vertices on ${converted?.name ?? primaryNode.name}`);
    return;
  }

  if (primaryNode.type === "vectorNetwork") {
    host.updateSelection(
      host.selectionSnapshot({
        selectedNodeIds: [primaryNode.id],
        primarySelectedNodeId: primaryNode.id,
        mode: "select",
        selectedVertexId: undefined,
        selectedVertexIds: [],
        selectedEdgeIds: [],
        selectedHandle: undefined,
      }),
      true,
    );
    uiStore.patchAnimation({ vectorTargetNodeId: primaryNode.id });
    uiStore.setMessage(`Targeting vertices on ${primaryNode.name}`);
    return;
  }

  uiStore.setMessage("Select a shape or vector network to target");
}

export function exitVectorTargeting(host: EditorCommandHost): void {
  uiStore.patchAnimation({ vectorTargetNodeId: undefined });
  host.updateSelection(
    host.selectionSnapshot({ selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined }),
    true,
  );
  uiStore.setMessage("Exited vector targeting");
}

export function clearVectorTargetingSelection(host: EditorCommandHost): void {
  host.updateSelection(
    host.selectionSnapshot({ selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined }),
    true,
  );
}
